import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { FwdRollMutableRowModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollMutableRowModel";
import { Subject } from "rxjs";
import { FwdRollChangeImpl } from "shared/cubiq-model-multi-asset";

const IDLE_INTERVAL_DEFAULT = 400;
const MAX_INTERVAL_DEFAULT = 2000;

/**
 * Change buffer that manages a growing list of pending changes until updates are enabled and
 * either of the two timer have fired - one time is the max permitted dureation beween changes
 * and the second is the maximum wait time
 */
export class FwdRollChangeBuffer {
  // Subject for emitting  updates
  private rowChangesSource = new Subject<FwdRollChangeImpl[]>();

  /**Public observable that emits events for updates to be pushed immedaiesly to the server */
  public rowChanges$ = this.rowChangesSource.asObservable();

  /** Min idle time to wait before pushing an update */
  private idleInterval: number;
  /** Max time to wait form receipt of girst update before pushing, regardless of update frequency */
  private maxInterval: number;

  /** List of pending updates to go to serve ron next output */
  private pendingUpdates: FwdRollMutableRowModel[];

  /** Promise that will fullfill when server updates are enabled - recreated everytime changes are output */
  private updateEnabled: Promise<void>;

  /** function called when server updates are allowed which fulfills hte promise   */
  private updateResolver: () => void;

  /** Promise that will fullfill when either of the two timers fire */
  private idleMaxTimeInterval: Promise<void>;
  /** function called when one of the timers fire   */
  private idleMaxTimeResolver: () => void;
  /** ID of current idle timer - id changes every time a new change arrives */
  private maxTimerId;
  /** ID of current max timer - timer is created once per batch of updates when first change is added */
  private idleTimerId;

  /** Has a server update been scheduled (equiv ro pendingUpdates.length ==1) */
  private updateScheduled: boolean = false;

  constructor() {
    this.pendingUpdates = [];
    this.idleInterval = IDLE_INTERVAL_DEFAULT;
    this.maxInterval = MAX_INTERVAL_DEFAULT;

    // Default is that updates are enables from the start
    this.updateEnabled = Promise.resolve();
  }

  public reset() {
    this.clearTimers();
    this.pendingUpdates.length = 0;

    this.updateEnabled = Promise.resolve();
    this.updateResolver = null;

    this.idleMaxTimeInterval = null;
    this.idleMaxTimeResolver = null;
  }

  /**
   * Clear all times when no update is pending
   */
  private clearTimers(): void {
    if (this.idleTimerId) {
      clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }
    if (this.maxTimerId) {
      clearTimeout(this.maxTimerId);
      this.maxTimerId = null;
    }
  }

  /**
   * Called when a response to an emitted update is received by client and updates are again enabled. If there are changes
   * queued up in the interim, this may trigger their emission if either of the timers have also fired
   */
  public outputEnabled() {
    if (this.updateResolver) {
      this.updateResolver();
      this.updateResolver = null;
    }
  }

  /**
   * Are there pending requests locally or emitted
   * @returns
   */
  public hasPendingUpdates(): boolean {
    return this.pendingUpdates.length > 0 || this.updateResolver != null;
  }

  /**
   * Apply a single model change
   * @param update
   */
  public processModelUpdate(update: FwdRollMutableRowModel): void {
    this.pendingUpdates.push(update);

    this.schedulePendingUpdates();
  }

  private schedulePendingUpdates() {
    if (this.idleTimerId) {
      clearTimeout(this.idleTimerId);
    }

    this.idleTimerId = setTimeout(() => this.idleTimeIntervalExpired(), this.idleInterval);
    if (!this.updateScheduled) {
      this.maxTimerId = setTimeout(() => this.maxTimeIntervalExpired(), this.maxInterval);
      // Set up promise for idle and max timeout
      this.idleMaxTimeInterval = new Promise<void>((res) => {
        this.idleMaxTimeResolver = res;
      });

      Promise.all([this.idleMaxTimeInterval, this.updateEnabled]).then(() => {
        logger.out.info("Promises all fulfilled");
        this.uploadChanges();
      });
      this.updateScheduled = true;
    }
  }

  private idleTimeIntervalExpired(): void {
    logger.out.info("Idle update timer expired");
    this.idleTimerId = 0;
    if (this.idleMaxTimeResolver) {
      this.idleMaxTimeResolver();
      this.idleMaxTimeResolver = null;
    }
  }
  private maxTimeIntervalExpired(): void {
    logger.out.info("Max update timer expired");
    this.maxTimerId = 0;
    if (this.idleMaxTimeResolver) {
      this.idleMaxTimeResolver();
      this.idleMaxTimeResolver = null;
    }
  }

  /**
   * Flaten all changes into single request for the server, and ensure that we
   * are ready for all requests that are due to arrive in the interim
   */
  private uploadChanges() {
    // We now have a pending update so ensure the promise is set up
    this.updateEnabled = new Promise<void>((res) => {
      this.updateResolver = res;
    });
    // Set false so next update will recreate the max timer and wait to push
    this.updateScheduled = false;

    const flattenedUpdates = this.flattenUpdates(this.pendingUpdates);
    this.clearTimers();
    this.pendingUpdates.length = 0;
    this.rowChangesSource.next(flattenedUpdates);
  }

  /**
   * Remove duplicate rows, only using the latest
   * @param updates
   * @returns
   */
  private flattenUpdates(updates: FwdRollMutableRowModel[]): FwdRollChangeImpl[] {
    const retUpdates = [];
    const processedKeys = {};
    for (let i = updates.length - 1; i >= 0; i--) {
      const update = updates[i];
      const key = update.rowId;
      if (!processedKeys[key]) {
        processedKeys[key] = true;
        let change = new FwdRollChangeImpl();
        change.rowId = update.rowId;
        change.fwdMarkup = update.fwdMarkup;
        change.marketForwardPts = update.marketForwardPts;
        change.rowType = update.rowType;
        change.lockedForUserPricing = update.lockedForUserPricing;

        retUpdates.push(change);
      }
    }
    logger.out.info(
      `Emitting changes: ${updates.length} events, ${retUpdates.length} server changes`,
    );
    return retUpdates;
  }
}
